import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import groupBy from '@teetsh/app/src/utils/arrays/groupBy';
import { format } from '@teetsh/app/src/utils/time/format';
import parseISO from '@teetsh/app/src/utils/time/parseISO';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import formatISO from '@teetsh/app/src/utils/time/formatISO';
import { useCJHistory } from 'containers/CahierJournal/queries/historyAtoms';
import { deserializeEvent } from 'containers/CahierJournal/queries/serializer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import serializeDateToServer from '@teetsh/app/src/utils/time/serializeDateToServer';
import { useTranslation } from 'react-i18next';

interface Props {
  edtId: string;
  from: Date;
  to: Date;
}

export function useTransfertEDTEventsToCJ(): [
  (t: Props) => Promise<any>,
  boolean,
] {
  const { schoolyear, school } = useSchoolSchoolyear();
  const user = useUser();

  const { t } = useTranslation(['emploiDuTemps']);
  const { notifyError, notifyWarning } = useNotification();
  const { addToPast, resetFuture } = useCJHistory();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: ({ from, to, edtId }: Props) => {
      const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/emplois-du-temps/${edtId}/transfert`;
      return request(requestURL, {
        method: 'POST',
        body: {
          from: formatISO(serializeDateToServer(from, user.location)),
          to: formatISO(serializeDateToServer(to, user.location)),
        },
      });
    },

    onSuccess: ({ data: cjEvents }) => {
      const dates = Object.keys(
        groupBy(cjEvents, e => format(parseISO(e.start), 'fr-FR')),
      );
      if (dates.length === 0) {
        notifyWarning({
          title: t('emploiDuTemps:messages.seance.noSeance', {
            schoolName: school.name,
            schoolyear: schoolyear.label,
          }),
        });
        return;
      }

      addToPast({
        payload: cjEvents.map(ev => deserializeEvent(ev, user.location)),
        action: 'CREATE',
      });
      resetFuture();
      dates.forEach(d => {
        queryClient.invalidateQueries({
          queryKey: [schoolyear.id, school.id, EVENTS_KEY, d],
        });
      });
    },
    onError: () => {
      notifyError({
        title: t('emploiDuTemps:messages.seance.copyError'),
      });
    },
  });
  return [mutateAsync, isLoading];
}
